import { MotionConfig, motion, useReducedMotion } from "framer-motion";
import type { Outbreak } from "#/server/data-fetchers.js";
import { formatDate, type TranslateFn } from "#/lib/i18n.js";

type Props = {
  outbreak: Outbreak;
  locale: string;
  t: TranslateFn;
};

const PARENT_VARIANTS = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const CHILD_VARIANTS = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } },
};

export function Hero({ outbreak, locale, t }: Props) {
  return (
    <MotionConfig reducedMotion="user">
      <HeroInner outbreak={outbreak} locale={locale} t={t} />
    </MotionConfig>
  );
}

function HeroInner({ outbreak, locale, t }: Props) {
  const reducedMotion = useReducedMotion();

  const startedDate = formatDate(outbreak.startedAt, locale, {
    dateStyle: "long",
  });
  const riskLabel = t(`risk.${outbreak.whoClassification}`);

  // When reduced motion is preferred, skip the stagger entirely and
  // render content immediately. We still wrap in motion divs so the
  // tree is identical between SSR and client (avoids hydration drift).
  const initial = reducedMotion ? "visible" : "hidden";

  return (
    <section
      aria-labelledby="hero-heading"
      className="hero-bg"
      style={{
        position: "relative",
        paddingBlock: "3.5rem",
        paddingInline: "1.5rem",
        borderBlockEnd: "1px solid var(--color-border-subtle)",
      }}
    >
      <motion.div
        initial={initial}
        animate="visible"
        variants={PARENT_VARIANTS}
        style={{
          maxWidth: "76rem",
          marginInline: "auto",
          display: "grid",
          gap: "1.25rem",
        }}
      >
        <motion.h1
          id="hero-heading"
          variants={CHILD_VARIANTS}
          style={{
            margin: 0,
            fontFamily: "var(--font-sans)",
            fontWeight: 700,
            fontSize: "clamp(2rem, 5vw, 3.25rem)",
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
            color: "var(--color-fg-default)",
          }}
        >
          {outbreak.name}
        </motion.h1>

        <motion.p
          variants={CHILD_VARIANTS}
          style={{
            margin: 0,
            fontSize: "clamp(1rem, 1.4vw, 1.25rem)",
            lineHeight: 1.55,
            maxWidth: "60ch",
            color: "var(--color-fg-muted)",
            fontWeight: 400,
          }}
        >
          {outbreak.summary}
        </motion.p>

        <motion.div
          variants={CHILD_VARIANTS}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            flexWrap: "wrap",
            fontSize: "0.875rem",
            color: "var(--color-fg-muted)",
            fontFamily: "var(--font-mono)",
          }}
        >
          <span>
            {t("hero.metaPrefix", { date: startedDate })}
          </span>
          <span aria-hidden="true">·</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
            {t("hero.riskPrefix")}{" "}
            <span
              className="who-pill"
              data-risk={outbreak.whoClassification}
              role="status"
            >
              {riskLabel}
            </span>
          </span>
        </motion.div>

        <motion.div
          variants={CHILD_VARIANTS}
          style={{
            display: "flex",
            gap: "0.75rem",
            flexWrap: "wrap",
            marginBlockStart: "0.5rem",
          }}
        >
          <a
            href="/subscribe"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "44px",
              paddingInline: "1.25rem",
              paddingBlock: "0.75rem",
              borderRadius: "0.375rem",
              background: "var(--color-accent-emergency)",
              color: "var(--color-accent-emergency-fg)",
              fontWeight: 600,
              textDecoration: "none",
              transition: "filter 150ms ease, background-color 150ms ease",
            }}
          >
            {t("hero.ctaSubscribe")}
          </a>
          <a
            href="#timeline"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "44px",
              paddingInline: "1.25rem",
              paddingBlock: "0.75rem",
              borderRadius: "0.375rem",
              background: "transparent",
              color: "var(--color-fg-default)",
              border: "1px solid var(--color-border-default)",
              fontWeight: 600,
              textDecoration: "none",
              transition: "background-color 150ms ease",
            }}
          >
            {t("hero.ctaTimeline")}
          </a>
          <a
            href="/download/android"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "44px",
              paddingInline: "1.25rem",
              paddingBlock: "0.75rem",
              borderRadius: "0.375rem",
              background: "transparent",
              color: "var(--color-fg-muted)",
              border: "1px solid var(--color-border-subtle)",
              fontWeight: 500,
              textDecoration: "none",
              transition: "background-color 150ms ease",
            }}
          >
            {t("hero.ctaGetApp")}
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
}
